import React from 'react';
import { Briefcase, Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Experience as ExperienceType } from '../types';
import { DateInput } from '../components/DateInput';

interface ExperienceProps {
  experiences: ExperienceType[];
  setExperiences: React.Dispatch<React.SetStateAction<ExperienceType[]>>;
}

export function Experience({ experiences, setExperiences }: ExperienceProps) {
  const addExperience = () => {
    setExperiences([...experiences, {
      id: experiences.length + 1,
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
      isCurrentJob: false
    }]);
  };

  const removeExperience = (id: number) => {
    setExperiences(experiences.filter(exp => exp.id !== id));
  };

  const updateExperience = (id: number, field: keyof ExperienceType, value: any) => {
    setExperiences(experiences.map(exp => 
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-semibold flex items-center gap-2">
        <Briefcase className="w-5 h-5" />
        Work Experience
      </h2>

      <AnimatePresence>
        {experiences.map((exp) => (
          <motion.div
            key={exp.id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-4 p-4 border border-gray-200 rounded-lg hover:border-indigo-300 
              transition-colors duration-200"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  type="text"
                  value={exp.company}
                  onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Position</label>
                <input
                  type="text"
                  value={exp.position}
                  onChange={(e) => updateExperience(exp.id, 'position', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>

              <DateInput
                label="Start Date"
                value={exp.startDate}
                onChange={(value) => updateExperience(exp.id, 'startDate', value)}
                required
              />

              <DateInput
                label="End Date"
                value={exp.endDate}
                onChange={(value) => updateExperience(exp.id, 'endDate', value)}
                showCurrent
                isCurrent={exp.isCurrentJob}
                onCurrentChange={(value) => {
                  updateExperience(exp.id, 'isCurrentJob', value);
                  if (value) {
                    updateExperience(exp.id, 'endDate', '');
                  }
                }}
              />

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={exp.description}
                  onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                    focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
              </div>
            </div>

            {experiences.length > 1 && (
              <motion.button
                type="button"
                onClick={() => removeExperience(exp.id)}
                className="text-red-600 hover:text-red-700 flex items-center gap-1"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 className="w-4 h-4" />
                Remove Experience
              </motion.button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={addExperience}
        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Plus className="w-4 h-4" />
        Add Another Experience
      </motion.button>
    </motion.div>
  );
}